import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FiTrendingUp, FiUsers, FiMapPin, FiAward, FiClock, FiTarget, FiZap, FiStar, FiChevronUp, FiChevronDown, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import TimeRangeSelector from '../components/TimeRangeSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Statistics = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState({ type: 'preset', days: '30' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'completions', direction: 'desc' });

  // Challenge performance table state
  const [perfPage, setPerfPage] = useState(1);
  const [perfSearch, setPerfSearch] = useState('');
  const [perfSearchInput, setPerfSearchInput] = useState('');
  const [perfSortBy, setPerfSortBy] = useState('completions');
  const [perfSortOrder, setPerfSortOrder] = useState('desc');

  // Build query params from time range
  const getQueryParams = () => {
    if (timeRange.type === 'custom') {
      return `startDate=${timeRange.startDate}&endDate=${timeRange.endDate}`;
    }
    return `days=${timeRange.days}`;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cms_accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Build query string for API calls
  const queryParams = useMemo(() => getQueryParams(), [timeRange]);

  // Existing data fetches
  const { data: challengeStats, isLoading: loadingChallenges } = useQuery(
    ['challengeStats', queryParams],
    () => axios.get(`${API_URL}/analytics/challenges?${queryParams}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: userBehavior, isLoading: loadingBehavior } = useQuery(
    ['userBehavior', queryParams, selectedCategory],
    () => axios.get(`${API_URL}/analytics/user-behavior?${queryParams}&category=${selectedCategory}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: geographicData, isLoading: loadingGeo } = useQuery(
    ['geographicData', queryParams],
    () => axios.get(`${API_URL}/analytics/geographic?${queryParams}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: engagementMetrics, isLoading: loadingEngagement } = useQuery(
    ['engagementMetrics', queryParams],
    () => axios.get(`${API_URL}/analytics/engagement?${queryParams}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: timePatterns, isLoading: loadingTime } = useQuery(
    ['timePatterns', queryParams],
    () => axios.get(`${API_URL}/analytics/time-patterns?${queryParams}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  // New data fetches
  const { data: ratingStats, isLoading: loadingRatings } = useQuery(
    ['ratingStats', timeRange],
    () => axios.get(`${API_URL}/analytics/ratings`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: nftStats, isLoading: loadingNft } = useQuery(
    ['nftStats', timeRange],
    () => axios.get(`${API_URL}/analytics/nft-stats`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  const { data: insightsData, isLoading: loadingInsights } = useQuery(
    ['analyticsInsights', timeRange],
    () => axios.get(`${API_URL}/analytics/insights`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  // Challenge performance table data (paginated)
  const { data: challengePerfData, isLoading: loadingChallengePerf } = useQuery(
    ['challengePerformance', queryParams, perfPage, perfSearch, perfSortBy, perfSortOrder],
    () => axios.get(
      `${API_URL}/analytics/challenge-performance?${queryParams}&page=${perfPage}&limit=20&search=${encodeURIComponent(perfSearch)}&sortBy=${perfSortBy}&sortOrder=${perfSortOrder}`,
      { headers: getAuthHeaders() }
    ).then(res => res.data)
  );

  // Chart colors
  const COLORS = ['#11827E', '#C53B8A', '#1ab7b1', '#e845a0', '#4dc7c3', '#ed6eb5', '#0e6b68', '#9e2f6e'];
  const NFT_RARITY_COLORS = { Common: '#9ca3af', Uncommon: '#22c55e', Rare: '#a855f7', Epic: '#f59e0b', Legendary: '#ef4444' };
  const DIFFICULTY_COLORS = { Easy: '#22c55e', Moderate: '#eab308', Hard: '#f97316', Expert: '#ef4444' };

  // Insight card border colors
  const insightBorderColors = {
    yellow: 'border-l-yellow-400',
    purple: 'border-l-purple-500',
    blue: 'border-l-blue-500',
    orange: 'border-l-orange-400',
    teal: 'border-l-teal-500'
  };

  const insightIcons = {
    star: FiStar,
    gem: FiAward,
    calendar: FiClock,
    lightbulb: FiZap,
    clock: FiClock,
    users: FiUsers
  };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedRegionalData = React.useMemo(() => {
    const data = geographicData?.regionalPerformance || [];
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = parseFloat(aValue);
      const numB = parseFloat(bValue);

      return sortConfig.direction === 'asc'
        ? numA - numB
        : numB - numA;
    });

    return sorted;
  }, [geographicData, sortConfig]);

  // Stat cards
  const statCards = [
    {
      title: t('cms.statistics.completionRate'),
      value: challengeStats?.totalCompletions || 0,
      change: challengeStats?.completionsChange || 0,
      icon: FiTarget,
      color: 'purple'
    },
    {
      title: t('cms.statistics.dailyActive'),
      value: engagementMetrics?.activeUsers || 0,
      change: engagementMetrics?.activeUsersChange || 0,
      icon: FiUsers,
      color: 'blue'
    },
    {
      title: t('cms.statistics.avgCompletionTime'),
      value: engagementMetrics?.avgCompletionsPerDay || 0,
      change: engagementMetrics?.avgCompletionsChange || 0,
      icon: FiClock,
      color: 'green'
    },
    {
      title: t('cms.statistics.popularChallenges'),
      value: challengeStats?.popularChallengesCount || 0,
      change: challengeStats?.popularityChange || 0,
      icon: FiTrendingUp,
      color: 'pink'
    }
  ];

  const getColorClass = (color) => {
    const colors = {
      purple: 'from-primary-500 to-primary-600',
      blue: 'from-primary-400 to-primary-500',
      green: 'from-primary-300 to-primary-400',
      pink: 'from-secondary-500 to-secondary-600'
    };
    return colors[color] || colors.purple;
  };

  // Sortable table header component
  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th
      onClick={() => handleSort(sortKey)}
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none ${className}`}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  // NFT funnel bar component
  const FunnelBar = ({ label, value, maxValue, color }) => {
    const pct = maxValue > 0 ? (value / maxValue * 100) : 0;
    return (
      <div className="flex items-center space-x-3">
        <span className="text-sm text-gray-600 w-32 text-right">{label}</span>
        <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
          <div
            className="h-6 rounded-full flex items-center justify-end pr-2 transition-all"
            style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: color }}
          >
            <span className="text-xs font-semibold text-white">{value}</span>
          </div>
        </div>
        <span className="text-sm text-gray-500 w-12">{pct.toFixed(0)}%</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.statistics')}</h1>
            <p className="text-gray-600">{t('cms.statistics.subtitle')}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <TimeRangeSelector
            value={timeRange}
            onChange={setTimeRange}
            showComparison={false}
          />
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    <p className={`text-sm mt-2 ${stat.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stat.change >= 0 ? '+' : ''}{stat.change}% vs previous period
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${getColorClass(stat.color)}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Insights */}
      {!loadingInsights && insightsData?.insights?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.trends')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insightsData.insights.map((insight, index) => {
              const InsightIcon = insightIcons[insight.icon] || FiZap;
              const borderColor = insightBorderColors[insight.color] || 'border-l-gray-400';
              return (
                <div key={index} className={`border-l-4 ${borderColor} bg-gray-50 rounded-r-lg p-4`}>
                  <div className="flex items-start space-x-3">
                    <InsightIcon className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{insight.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Challenge Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.popularChallenges')}</h2>
          {loadingChallenges ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={challengeStats?.topChallenges || []} margin={{ top: 5, right: 20, bottom: 80, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} interval={0} />
                <YAxis />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border rounded-lg shadow-lg">
                          <p className="font-semibold text-gray-900">{data.fullName || data.name}</p>
                          <p className="text-sm text-gray-600">{data.completions} completions</p>
                          <p className="text-xs text-primary-600 mt-1">Click for details</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="completions"
                  fill="#11827E"
                  onClick={(data) => {
                    if (data && data.id) {
                      navigate(`/statistics/challenge/${data.id}`);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.completionRateByDifficulty')}</h2>
          {loadingChallenges ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (challengeStats?.difficultyBreakdown || []).length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-400">
              {t('cms.statistics.noDifficultyData')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={challengeStats?.difficultyBreakdown || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(challengeStats?.difficultyBreakdown || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DIFFICULTY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* User Behavior Patterns */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('cms.statistics.userEngagementTrend')}</h2>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">{t('cms.statistics.allCategories')}</option>
            <option value="urban">{t('cms.statistics.categoryUrban')}</option>
            <option value="nature">{t('cms.statistics.categoryNature')}</option>
            <option value="historic">{t('cms.statistics.categoryHistoric')}</option>
            <option value="adventure">{t('cms.statistics.categoryAdventure')}</option>
            <option value="water">{t('cms.statistics.categoryWater')}</option>
            <option value="park">{t('cms.statistics.categoryPark')}</option>
          </select>
        </div>
        {loadingBehavior ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={userBehavior?.dailyEngagement || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="newUsers" stackId="1" stroke="#11827E" fill="#11827E" name="New Users" />
              <Area type="monotone" dataKey="returningUsers" stackId="1" stroke="#C53B8A" fill="#C53B8A" name="Returning Users" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Time Patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.peakActivityHours')}</h2>
          {loadingTime ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={timePatterns?.hourlyActivity || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="activity" stroke="#11827E" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.activityByDayOfWeek')}</h2>
          {loadingTime ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={timePatterns?.weeklyActivity || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="activity" fill="#C53B8A" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Geographic Heatmap */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.geographicActivityHeatmap')}</h2>
        {loadingGeo ? (
          <div className="h-96 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="h-96 rounded-lg overflow-hidden">
            <MapContainer
              center={[52.20, 12.82]}
              zoom={9}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {(geographicData?.hotspots || []).map((point, index) => (
                <CircleMarker
                  key={index}
                  center={[point.latitude, point.longitude]}
                  radius={Math.sqrt(point.activity) * 6}
                  fillColor="#11827E"
                  color="#fff"
                  weight={2}
                  opacity={0.9}
                  fillOpacity={0.7}
                >
                  <Popup>
                    <div>
                      <strong>{point.name}</strong>
                      <br />
                      Activity: {point.activity} completions
                      <br />
                      Category: {point.category}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>

      {/* Category Preferences + Tourism Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.challengeCategoriesPerformance')}</h2>
          {loadingBehavior ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart outerRadius="70%" data={userBehavior?.categoryPreferences || []}>
                <PolarGrid />
                <PolarAngleAxis dataKey="category" />
                <PolarRadiusAxis />
                <Radar name="Completions" dataKey="completions" stroke="#11827E" fill="#11827E" fillOpacity={0.6} />
                <Tooltip wrapperStyle={{ zIndex: 10 }} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.overview')}</h2>
          {loadingEngagement ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
                <span className="text-gray-700">{t('cms.statistics.userRetention')} (7-day)</span>
                <span className="text-2xl font-bold text-primary-600">{engagementMetrics?.retention7Day || 0}%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-primary-100 rounded-lg">
                <span className="text-gray-700">{t('cms.statistics.userRetention')} (30-day)</span>
                <span className="text-2xl font-bold text-primary-700">
                  {engagementMetrics?.retention30Day === 'N/A' ? 'N/A' : `${engagementMetrics?.retention30Day || 0}%`}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                <span className="text-gray-700">{t('cms.statistics.avgChallengesPerUser')}</span>
                <span className="text-2xl font-bold text-secondary-600">{engagementMetrics?.avgChallengesPerUser || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary-100 rounded-lg">
                <span className="text-gray-700">{t('cms.statistics.crossRegionExploration')}</span>
                <span className="text-2xl font-bold text-secondary-700">{engagementMetrics?.crossRegionRate || 0}%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary-200 rounded-lg">
                <span className="text-gray-700">{t('cms.statistics.nftClaimRate')}</span>
                <span className="text-2xl font-bold text-secondary-800">{engagementMetrics?.nftClaimRate || 0}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rating Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('cms.statistics.ratingDistribution')}</h2>
            <div className="text-right">
              <p className="text-3xl font-bold text-primary-600">{ratingStats?.overallAverage || 0}</p>
              <p className="text-xs text-gray-500">{ratingStats?.totalRatings || 0} total ratings</p>
            </div>
          </div>
          {loadingRatings ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={ratingStats?.distribution || []} layout="vertical" margin={{ left: 40, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="stars" tickFormatter={(v) => `${v} star${v > 1 ? 's' : ''}`} />
                <Tooltip formatter={(value) => [value, 'Ratings']} />
                <Bar dataKey="count" fill="#eab308" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.avgRatingByCategory')}</h2>
          {loadingRatings ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (ratingStats?.byType || []).length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No rating data by category yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={ratingStats?.byType || []} layout="vertical" margin={{ left: 60, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 5]} />
                <YAxis type="category" dataKey="type" tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
                <Tooltip formatter={(value) => [value, 'Avg Rating']} />
                <Bar dataKey="avgRating" radius={[0, 4, 4, 0]}>
                  {(ratingStats?.byType || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* NFT Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.nftRarityDistribution')}</h2>
          {loadingNft ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (nftStats?.rarityDistribution || []).length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-400">
              {t('cms.statistics.noNftData')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={nftStats?.rarityDistribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(nftStats?.rarityDistribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={NFT_RARITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.nftMintingFunnel')}</h2>
          {loadingNft ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : !nftStats?.funnel ? (
            <div className="h-80 flex items-center justify-center text-gray-400">
              {t('cms.statistics.noFunnelData')}
            </div>
          ) : (
            <div className="space-y-5 pt-4">
              <FunnelBar
                label={t('cms.statistics.completions')}
                value={nftStats.funnel.totalCompletions}
                maxValue={nftStats.funnel.totalCompletions}
                color="#11827E"
              />
              <FunnelBar
                label={t('cms.statistics.nftEarned')}
                value={nftStats.funnel.nftEarned}
                maxValue={nftStats.funnel.totalCompletions}
                color="#1ab7b1"
              />
              <FunnelBar
                label={t('cms.statistics.nftMinted')}
                value={nftStats.funnel.nftMinted}
                maxValue={nftStats.funnel.totalCompletions}
                color="#C53B8A"
              />
              <FunnelBar
                label={t('cms.statistics.custodial')}
                value={nftStats.funnel.custodialActivated}
                maxValue={nftStats.funnel.totalCompletions}
                color="#a855f7"
              />
              <p className="text-xs text-gray-400 text-center mt-2">
                {t('cms.statistics.funnelDescription')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* NFT Minting by Category */}
      {!loadingNft && (nftStats?.mintingByType || []).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('cms.statistics.nftMintingByType')}</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={nftStats.mintingByType} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="completions" fill="#11827E" name="Completions" />
              <Bar dataKey="nftEarned" fill="#1ab7b1" name="NFT Earned" />
              <Bar dataKey="nftMinted" fill="#C53B8A" name="NFT Minted" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Challenge Performance Table (Paginated) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('cms.statistics.charts')}</h2>
            {challengePerfData?.pagination && (
              <p className="text-sm text-gray-500">
                {challengePerfData.pagination.totalCount} challenges total
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cms.statistics.searchChallenges')}
                value={perfSearchInput}
                onChange={(e) => setPerfSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPerfSearch(perfSearchInput);
                    setPerfPage(1);
                  }
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
              />
            </div>
            <button
              onClick={() => { setPerfSearch(perfSearchInput); setPerfPage(1); }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('cms.common.search')}
            </button>
            {perfSearch && (
              <button
                onClick={() => { setPerfSearch(''); setPerfSearchInput(''); setPerfPage(1); }}
                className="px-3 py-2 text-gray-600 hover:text-gray-800"
              >
                {t('cms.common.clear')}
              </button>
            )}
          </div>
        </div>

        {loadingChallengePerf ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => {
                        if (perfSortBy === 'name') {
                          setPerfSortOrder(perfSortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setPerfSortBy('name');
                          setPerfSortOrder('asc');
                        }
                        setPerfPage(1);
                      }}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{t('cms.statistics.tableChallenge')}</span>
                        {perfSortBy === 'name' && (perfSortOrder === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />)}
                      </div>
                    </th>
                    {[
                      { key: 'visits', label: t('cms.statistics.tableVisits') },
                      { key: 'completions', label: t('cms.statistics.tableCompletions') },
                      { key: 'points', label: t('cms.statistics.tablePoints') },
                      { key: 'difficulty', label: t('cms.statistics.tableDifficulty') },
                      { key: 'completion_rate', label: t('cms.statistics.tableCompletionRate') },
                      { key: 'trend', label: t('cms.statistics.tableTrend') }
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => {
                          if (perfSortBy === col.key) {
                            setPerfSortOrder(perfSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setPerfSortBy(col.key);
                            setPerfSortOrder('desc');
                          }
                          setPerfPage(1);
                        }}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      >
                        <div className="flex items-center space-x-1">
                          <span>{col.label}</span>
                          {perfSortBy === col.key && (perfSortOrder === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(challengePerfData?.challenges || []).map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/statistics/challenge/${row.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-800">
                        {row.name.length > 35 ? row.name.substring(0, 35) + '...' : row.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.visits}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.completions}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.points}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          row.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                          row.difficulty === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                          row.difficulty === 'Hard' ? 'bg-orange-100 text-orange-800' :
                          row.difficulty === 'Expert' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {row.difficulty}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.completionRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${row.trend >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {row.trend >= 0 ? '+' : ''}{row.trend}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {challengePerfData?.pagination && challengePerfData.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {t('cms.statistics.showingOf', {
                    start: ((perfPage - 1) * 20) + 1,
                    end: Math.min(perfPage * 20, challengePerfData.pagination.totalCount),
                    total: challengePerfData.pagination.totalCount
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPerfPage(p => Math.max(1, p - 1))}
                    disabled={perfPage === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, challengePerfData.pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      const totalPages = challengePerfData.pagination.totalPages;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (perfPage <= 3) {
                        pageNum = i + 1;
                      } else if (perfPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = perfPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPerfPage(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            perfPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPerfPage(p => Math.min(challengePerfData.pagination.totalPages, p + 1))}
                    disabled={perfPage === challengePerfData.pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Statistics;
